/**
 * PreviewCanvas - Real-time video composition preview
 * 
 * Renders the video composition using Canvas for accurate WYSIWYG preview
 * that matches the final FFmpeg output.
 */

import React, { useRef, useEffect, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Play, Pause, RotateCcw, Volume2, VolumeX } from 'lucide-react';
import { VideoCompositor } from '../../services/VideoCompositor';
import './PreviewCanvas.css';

export const PreviewCanvas = forwardRef(({
  videos,
  mainTitle,
  titleStyle,
  transitionType = 'fade',
  transitionDuration = 0.5,
  onTimeUpdate,
  onClipChange,
  width = 1080,
  height = 1920,
}, ref) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const compositorRef = useRef(null);

  // Store callbacks in refs to avoid re-creating compositor
  const onTimeUpdateRef = useRef(onTimeUpdate);
  const onClipChangeRef = useRef(onClipChange);

  // Update refs when callbacks change
  useEffect(() => {
    onTimeUpdateRef.current = onTimeUpdate;
  }, [onTimeUpdate]);

  useEffect(() => {
    onClipChangeRef.current = onClipChange;
  }, [onClipChange]);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false); // Start unmuted for audio
  const [isLoading, setIsLoading] = useState(false);
  const [scale, setScale] = useState(1);

  // Determine if user has added any videos (even if not yet loaded)
  const hasAnyVideos = videos && videos.some(v => v.url || v.previewUrl || v.isLoaded);

  // Track the canvas element to detect when React replaces it
  const lastCanvasRef = useRef(null);

  // Initialize compositor - and update canvas reference when React replaces it
  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;

    // Check if this is a NEW canvas element (React replaced it)
    if (compositorRef.current && lastCanvasRef.current !== canvas) {
      compositorRef.current.updateCanvas(canvas);
      lastCanvasRef.current = canvas;
      return;
    }

    // Skip re-initialization if compositor already exists with same dimensions
    if (compositorRef.current &&
      compositorRef.current.width === width &&
      compositorRef.current.height === height) {
      return;
    }

    // Initialize compositor

    // Destroy old compositor if exists
    if (compositorRef.current) {
      compositorRef.current.destroy();
    }

    const compositor = new VideoCompositor(canvas, {
      width,
      height,
    });

    compositor.onTimeUpdate = (time, duration) => {
      setCurrentTime(time);
      setTotalDuration(duration);
      if (onTimeUpdateRef.current) onTimeUpdateRef.current(time, duration);
    };

    compositor.onPlayStateChange = (playing) => {
      setIsPlaying(playing);
    };

    compositor.onClipChange = (clipIndex) => {
      if (onClipChangeRef.current) onClipChangeRef.current(clipIndex);
    };

    compositorRef.current = compositor;
    lastCanvasRef.current = canvas;

    return () => {
      if (compositorRef.current) {
        compositorRef.current.destroy();
        compositorRef.current = null;
      }
    };
  }, [width, height]); // Only depend on dimensions!

  // Update canvas reference on every render (in case React replaced the element)
  useEffect(() => {
    if (canvasRef.current && compositorRef.current && lastCanvasRef.current !== canvasRef.current) {
      compositorRef.current.updateCanvas(canvasRef.current);
      lastCanvasRef.current = canvasRef.current;
    }
  });

  // Update compositor config when props change
  useEffect(() => {
    if (!compositorRef.current) return;

    const titleLines = mainTitle ? mainTitle.split('\n').filter(l => l.trim()) : [];
    const combinedTitle = titleLines.join('\n');

    compositorRef.current.setConfig({
      mainTitle: combinedTitle,
      titleStyle: titleStyle || {},
      transitionType,
      transitionDuration,
    });

    // Re-render current frame
    compositorRef.current.renderFrame();
  }, [mainTitle, titleStyle, transitionType, transitionDuration]);

  // Update clips when videos change - debounced to prevent rapid updates
  useEffect(() => {
    if (!compositorRef.current) return;

    const loadedVideos = videos.filter(v => v.isLoaded && (v.previewUrl || v.url));
    const pendingVideos = videos.filter(v => (v.url || v.previewUrl) && !v.isLoaded);

    // Set loading true only if we have videos being loaded
    if (pendingVideos.length > 0) {
      setIsLoading(true);
    } else if (loadedVideos.length > 0) {
      setIsLoading(false);
    } else {
      // No videos at all - not loading
      setIsLoading(false);
    }

    // Debounce the update to prevent rapid re-renders
    const timeoutId = setTimeout(() => {
      if (compositorRef.current) {
        compositorRef.current.setClips(loadedVideos);
        const duration = compositorRef.current.getTotalDuration();
        setTotalDuration(duration);
      }
    }, 150); // Slightly longer debounce

    return () => clearTimeout(timeoutId);
  }, [videos]);

  // Calculate scale to fit container
  useEffect(() => {
    const updateScale = () => {
      if (!containerRef.current) return;

      const containerWidth = containerRef.current.clientWidth;
      // Account for playback controls height (~80px)
      const containerHeight = containerRef.current.clientHeight - 80;

      if (containerWidth === 0 || containerHeight <= 0) return;

      const scaleX = containerWidth / width;
      const scaleY = containerHeight / height;
      // Use more of the available space (0.98 instead of 0.95)
      const newScale = Math.max(Math.min(scaleX, scaleY, 1) * 0.98, 0.15);

      setScale(newScale);
    };

    // Delay initial call to ensure container is rendered
    const timerId = setTimeout(updateScale, 50);
    window.addEventListener('resize', updateScale);

    // Also update on container resize via ResizeObserver
    const resizeObserver = new ResizeObserver(updateScale);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      clearTimeout(timerId);
      window.removeEventListener('resize', updateScale);
      resizeObserver.disconnect();
    };
  }, [width, height]);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    play: () => compositorRef.current?.play(),
    pause: () => compositorRef.current?.pause(),
    seek: (time) => compositorRef.current?.seek(time),
    togglePlay: () => compositorRef.current?.togglePlay(),
    getCurrentTime: () => compositorRef.current?.getCurrentTime() || 0,
    getTotalDuration: () => compositorRef.current?.getTotalDuration() || 0,
  }));

  // Debounce play/pause to prevent double-clicks
  const lastPlayPauseTime = useRef(0);

  const handlePlayPause = useCallback((e) => {
    // Prevent event bubbling
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    // Debounce - ignore rapid clicks
    const now = Date.now();
    if (now - lastPlayPauseTime.current < 300) {
      return;
    }
    lastPlayPauseTime.current = now;

    if (compositorRef.current) {
      compositorRef.current.togglePlay();
    }
  }, []);

  const handleReset = useCallback(() => {
    if (compositorRef.current) {
      compositorRef.current.pause();
      compositorRef.current.seek(0);
    }
  }, []);

  const handleSeek = useCallback((e) => {
    if (!compositorRef.current) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const progress = x / rect.width;
    const time = progress * totalDuration;

    compositorRef.current.seek(time);
  }, [totalDuration]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 10);
    return `${mins}:${secs.toString().padStart(2, '0')}.${ms}`;
  };

  return (
    <div className="preview-canvas-container" ref={containerRef}>
      <div
        className="canvas-wrapper"
        style={{
          width: width * scale,
          height: height * scale,
        }}
      >
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          className="composition-canvas"
          style={{
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
        />

        {/* Show import message when no videos, loading when clips are being processed */}
        {!hasAnyVideos && (
          <div className="canvas-loading">
            <span>Import Videos to Preview</span>
          </div>
        )}

        {hasAnyVideos && isLoading && (
          <div className="canvas-loading">
            <div className="loading-spinner" />
            <span>Loading preview...</span>
          </div>
        )}

        {/* Click to play overlay */}
        {!isPlaying && !isLoading && (
          <div className="play-overlay" onClick={handlePlayPause}>
            <div className="play-button-large">
              <Play size={48} fill="currentColor" />
            </div>
          </div>
        )}
      </div>

      {/* Playback controls */}
      <div className="playback-controls">
        <div className="control-buttons">
          <button
            className="control-btn"
            onClick={handleReset}
            title="Reset"
          >
            <RotateCcw size={18} />
          </button>

          <button
            type="button"
            className="control-btn cyan-play-btn"
            onClick={handlePlayPause}
            title={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? <Pause size={20} /> : <Play size={20} fill="currentColor" />}
          </button>

          <button
            className="control-btn"
            onClick={() => {
              const newMuted = !isMuted;
              setIsMuted(newMuted);
              if (compositorRef.current) {
                compositorRef.current.setMuted(newMuted);
              }
            }}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
        </div>

        {/* Progress bar */}
        <div className="progress-container" onClick={handleSeek}>
          <div className="progress-bar">
            <div
              className="cyan-progress-fill"
              style={{ width: `${totalDuration > 0 ? (currentTime / totalDuration) * 100 : 0}%` }}
            />
            <div
              className="progress-handle"
              style={{ left: `${totalDuration > 0 ? (currentTime / totalDuration) * 100 : 0}%` }}
            />
          </div>
        </div>

        {/* Time display */}
        <div className="time-display">
          <span className="current-time">{formatTime(currentTime)}</span>
          <span className="time-separator">/</span>
          <span className="total-time">{formatTime(totalDuration)}</span>
        </div>
      </div>
    </div>
  );
});

PreviewCanvas.displayName = 'PreviewCanvas';

export default PreviewCanvas;

